import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ACCESS_LABELS, ACCESS_LEVELS, accessRank, PERMISSION_KEYS, PERMISSION_LABELS, type Access, type AccountView, type AuditEntryView, type Permissions, type StaffView } from '@lp/contracts';
import { KeyRound, Plus } from 'lucide-react';
import { useState } from 'react';
import { errorText, fieldProblems, get, patch, post } from '@/lib/api';
import { useCompanyUser } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useScope } from '@/lib/scope';
import { ClientSettings } from '@/components/client-settings';
import type { ClientProductView, DomainView } from '@lp/contracts';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader, Select, cn } from '@/components/ui';

const TONE: Record<Access, 'green' | 'blue' | 'gray'> = { edit: 'green', read: 'blue', none: 'gray' };

export function AccessBadge({ access }: { access: Access }) {
  const t = useT();
  return <Badge tone={TONE[access]}>{t(ACCESS_LABELS[access])}</Badge>;
}

/** the company user's home: who it is, for which account, and what it may do */
export function CompanyHomePage() {
  const t = useT();
  const me = useCompanyUser();
  if (!me) return null;
  return (
    <>
      <PageHeader title={`${t('Welcome')}, ${me.displayName}`} subtitle={`${me.client.nameEn} · ${me.client.nameKm}`} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('You')}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
            <dt className="text-ink-500">{t('Username')}</dt>
            <dd className="font-mono text-xs">{me.username}</dd>
            <dt className="text-ink-500">{t('Role')}</dt>
            <dd>{me.role === 'admin' ? t('Company admin') : (me.position ?? t('Staff'))}</dd>
            <dt className="text-ink-500">{t('Works for')}</dt>
            <dd>{me.account ? `${me.account.name} (${me.account.code})` : t('the company')}</dd>
          </dl>
        </Card>
        <Card title={t('What you may do')}>
          <ul className="space-y-1 text-sm" data-testid="my-permissions">
            {PERMISSION_KEYS.map((k) => (
              <li key={k} className="flex items-center justify-between gap-2">
                <span>{t(PERMISSION_LABELS[k])}</span>
                <AccessBadge access={me.permissions[k]} />
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </>
  );
}

/** a permissions editor: each key none / read / edit, never above the maker's own */
function PermissionsEditor({ value, max, onChange }: { value: Permissions; max: Permissions; onChange: (p: Permissions) => void }) {
  const t = useT();
  return (
    <table className="w-full text-xs">
      <tbody>
        {PERMISSION_KEYS.map((k) => (
          <tr key={k}>
            <td className="py-1 pr-2">{t(PERMISSION_LABELS[k])}</td>
            <td className="py-1">
              <Select value={value[k]} onChange={(e) => onChange({ ...value, [k]: e.target.value as Access })} className="h-7 text-xs" aria-label={t(PERMISSION_LABELS[k])}>
                {ACCESS_LEVELS.filter((a) => accessRank(a) <= accessRank(max[k])).map((a) => (
                  <option key={a} value={a}>
                    {t(ACCESS_LABELS[a])}
                  </option>
                ))}
              </Select>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const noneAll = (): Permissions => Object.fromEntries(PERMISSION_KEYS.map((k) => [k, 'none'])) as Permissions;

/** the company's staff (master prompt §13): who may do what; a maker gives only what it has, inside its branch */
export function StaffPage() {
  const t = useT();
  const qc = useQueryClient();
  const me = useCompanyUser();
  const scope = useScope();
  const canEdit = scope.can('staff', 'edit');
  const list = useQuery({ queryKey: ['company', 'staff'], queryFn: () => get<StaffView[]>('/company/staff') });
  const accounts = useQuery({ queryKey: ['client', scope.clientId, 'accounts'], queryFn: () => get<AccountView[]>('/company/accounts'), enabled: scope.can('accounts', 'read') });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StaffView | null>(null);
  const [shown, setShown] = useState<{ username: string; initialPassword: string } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['company', 'staff'] });
  const toggle = useMutation({ mutationFn: (s: StaffView) => patch<StaffView>(`/company/staff/${s.id}`, { status: s.status === 'active' ? 'disabled' : 'active' }), onSuccess: refresh });
  const reset = useMutation({ mutationFn: (s: StaffView) => post<{ username: string; initialPassword: string }>(`/company/staff/${s.id}/password`), onSuccess: setShown });
  if (!me) return null;
  return (
    <>
      <PageHeader
        title={t('Staff')}
        subtitle={t('Who may do what. Full edit, read only, or not allowed — nobody gives more than they have.')}
        actions={
          canEdit && (
            <Button icon={<Plus size={14} />} onClick={() => setCreating(true)}>
              {t('New staff')}
            </Button>
          )
        }
      />
      {shown && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="staff-password">
          <p className="mb-1 text-xs text-amber-900">{t('Shown only now — give it to the user privately. The old password no longer works.')}</p>
          <span className="font-mono">{shown.username}</span> · <span className="font-mono text-base font-semibold tracking-wider">{shown.initialPassword}</span>
        </div>
      )}
      <Card>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox text={errorText(list.error)} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {(list.data ?? []).map((s) => (
                  <tr key={s.id} className={cn('border-b border-ink-100 last:border-0', s.status !== 'active' && 'opacity-50')} data-testid={`staff-${s.username}`}>
                    <td className="py-2 pr-3">
                      <span className="font-medium">{s.displayName}</span> <span className="font-mono text-[11px] text-ink-500">{s.username}</span>
                      <span className="block text-xs text-ink-500">{s.role === 'admin' ? t('Company admin') : (s.position ?? t('Staff'))}</span>
                    </td>
                    <td className="py-2 pr-3 text-xs">{s.account ? `${s.account.name} (${s.account.code})` : t('the company')}</td>
                    <td className="py-2 pr-3">
                      <span className="flex flex-wrap gap-1">
                        {PERMISSION_KEYS.filter((k) => s.permissions[k] !== 'none').map((k) => (
                          <span key={k} className={cn('rounded px-1.5 py-0.5 text-[10px]', s.permissions[k] === 'edit' ? 'bg-emerald-50 text-emerald-700' : 'bg-sky-50 text-sky-700')}>
                            {k}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="py-2 text-right">
                      {canEdit && s.role !== 'admin' && s.id !== me.id && (
                        <span className="inline-flex gap-1">
                          <Button variant="ghost" onClick={() => setEditing(s)}>
                            {t('Permissions')}
                          </Button>
                          <Button variant="ghost" icon={<KeyRound size={12} />} onClick={() => reset.mutate(s)} disabled={reset.isPending} aria-label={`${t('New first password')} ${s.username}`} />
                          <Button variant="ghost" onClick={() => toggle.mutate(s)} disabled={toggle.isPending}>
                            {t(s.status === 'active' ? 'Disable' : 'Enable')}
                          </Button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(toggle.error || reset.error) && <ErrorBox text={errorText(toggle.error ?? reset.error)} />}
      </Card>
      {creating && (
        <NewStaff
          max={me.permissions}
          accounts={(accounts.data ?? []).filter((a) => a.status === 'active')}
          companyLevel={scope.companyLevel}
          onClose={() => setCreating(false)}
          onMade={(r) => {
            setCreating(false);
            setShown({ username: r.staff.username, initialPassword: r.initialPassword });
            refresh();
          }}
        />
      )}
      {editing && <EditStaff staff={editing} max={me.permissions} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </>
  );
}

function NewStaff({ max, accounts, companyLevel, onClose, onMade }: { max: Permissions; accounts: AccountView[]; companyLevel: boolean; onClose: () => void; onMade: (r: { staff: StaffView; initialPassword: string }) => void }) {
  const t = useT();
  const me = useCompanyUser()!;
  const [f, setF] = useState({ username: '', displayName: '', position: '', account: companyLevel ? '' : String(me.account?.id ?? '') });
  const [permissions, setPermissions] = useState<Permissions>(noneAll());
  const create = useMutation({
    mutationFn: () => post<{ staff: StaffView; initialPassword: string }>('/company/staff', { username: f.username, displayName: f.displayName, ...(f.position.trim() ? { position: f.position } : {}), accountId: f.account ? Number(f.account) : null, permissions }),
    onSuccess: onMade,
  });
  return (
    <Dialog
      title={t('New staff')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => create.mutate()} disabled={!f.username || !f.displayName || create.isPending}>
            {t('Create')}
          </Button>
        </>
      }
    >
      <Field label={t('Username')}>
        <Input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase() })} maxLength={40} autoComplete="off" />
      </Field>
      <Field label={t('Display name')}>
        <Input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} maxLength={80} />
      </Field>
      <Field label={t('Position')}>
        <Input value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })} maxLength={60} placeholder={t('Operation Manager')} />
      </Field>
      <Field label={t('Works for')}>
        <Select value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} className="w-full" aria-label={t('Works for')}>
          {companyLevel && <option value="">{t('the company')}</option>}
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.path.join(' / ')} — {a.name}
            </option>
          ))}
        </Select>
      </Field>
      <PermissionsEditor value={permissions} max={max} onChange={setPermissions} />
      {create.error && <ErrorBox text={[errorText(create.error), ...fieldProblems(create.error)].join(' — ')} />}
    </Dialog>
  );
}

function EditStaff({ staff, max, onClose, onSaved }: { staff: StaffView; max: Permissions; onClose: () => void; onSaved: () => void }) {
  const t = useT();
  const [permissions, setPermissions] = useState<Permissions>(staff.permissions);
  const [position, setPosition] = useState(staff.position ?? '');
  const save = useMutation({ mutationFn: () => patch<StaffView>(`/company/staff/${staff.id}`, { permissions, position: position.trim() || null }), onSuccess: onSaved });
  return (
    <Dialog
      title={`${t('Permissions')} — ${staff.displayName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <Field label={t('Position')}>
        <Input value={position} onChange={(e) => setPosition(e.target.value)} maxLength={60} />
      </Field>
      <PermissionsEditor value={permissions} max={max} onChange={setPermissions} />
      <p className="text-[11px] text-ink-500">{t('Lowering a user lowers everyone it made, all the way down.')}</p>
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}
    </Dialog>
  );
}

/** what happened in the company: who (and for which account), when, what, on what */
export function CompanyActivityPage() {
  const t = useT();
  const list = useQuery({ queryKey: ['company', 'activity'], queryFn: () => get<AuditEntryView[]>('/company/activity') });
  return (
    <>
      <PageHeader title={t('Activity')} subtitle={t('Every change in the company, with who made it and for which account.')} />
      <Card>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox text={errorText(list.error)} />
        ) : (
          <ul className="divide-y divide-ink-100 text-sm" data-testid="activity">
            {(list.data ?? []).map((e) => (
              <li key={e.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5">
                <span className="w-32 shrink-0 text-xs text-ink-500">{dateTime(e.at)}</span>
                <span className="font-mono text-xs">{e.action}</span>
                <span className="text-xs">{e.actor.username ?? t('System')}</span>
                {e.actor.kind === 'platform_user' && <Badge>{t('Platform')}</Badge>}
                <span className="text-xs text-ink-500">
                  {e.target.type} {e.target.id}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}

/** the company's products — with its own names (read only: the Super Admin installs products) */
export function CompanyProductsPage() {
  const t = useT();
  const list = useQuery({ queryKey: ['company', 'products'], queryFn: () => get<ClientProductView[]>('/company/products') });
  return (
    <>
      <PageHeader title={t('Products')} subtitle={t('The lottery products your company sells, each on its version.')} />
      <Card>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <ErrorBox text={errorText(list.error)} />
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {(list.data ?? [])
                .filter((p) => p.status === 'active')
                .map((p) => (
                  <tr key={p.productCode} className="border-b border-ink-100 last:border-0" data-testid={`company-product-${p.productCode}`}>
                    <td className="py-2 pr-3">
                      <span className="font-medium">{p.nameEn}</span> <span className="font-mono text-[11px] text-ink-500">{p.productCode}</span>
                      <span className="block text-xs text-ink-500">{p.nameKm}</span>
                    </td>
                    <td className="py-2 text-right">
                      <Badge>v{p.version}</Badge>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/** the company's settings (its own values where it may) and its addresses */
export function CompanySettingsPage() {
  const t = useT();
  const scope = useScope();
  const domains = useQuery({ queryKey: ['company', 'domains'], queryFn: () => get<DomainView[]>('/company/domains') });
  return (
    <>
      <PageHeader title={t('Settings')} subtitle={t('Each value with where it comes from. Your own values override what your package and template give.')} />
      <ClientSettings clientId={scope.clientId} />
      <Card title={t('Domains')} className="mt-4">
        {domains.isLoading ? (
          <Loading />
        ) : (
          <ul className="space-y-1 text-sm">
            {(domains.data ?? []).map((d) => (
              <li key={d.id} className="flex items-center gap-2">
                <span className="font-mono text-xs">{d.host}</span>
                <Badge tone={d.status === 'active' ? 'green' : 'gray'}>{t(d.status === 'active' ? 'Active' : d.status === 'disabled' ? 'Disabled' : 'Waiting for DNS')}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
